import { z } from 'zod'
import { coverageRuleInputSchema, coverageRuleSchema } from './coverage'
import { mediaTypeSchema, type MediaType } from './media'

/**
 * The stream (CLI-758): one per sub-account, read row by row in the Library and in aggregate in
 * Analytics. What feeds it is configured on the Configure page — topics with per-topic source
 * narrowing, coverage rules, and the mode — and stored in `streams` / `stream_topics` /
 * `coverage_rules.stream_id`. The row vocabulary below is the final one (Andre, 2026-09-16):
 * headline, media type, link, outlet, author, folder, topic, tags, date, sentiment,
 * classification, snippet, plus reach and the syndication fields the pages group on.
 *
 * The rows are what the analytics webapp's cache job built for this sub-account's reports —
 * coverage curated into reports (and, for its live-tier clients, the matcher's stream), with the
 * sentiment the job scored — read from the webapp backend's cached report payloads and never
 * computed here: Headline reads the topic cache the way the legacy webapp reads its own.
 */

export const SENTIMENTS = ['positive', 'neutral', 'negative', 'mixed'] as const
export const streamSentimentSchema = z.enum(SENTIMENTS)
export type StreamSentiment = z.infer<typeof streamSentimentSchema>

/** One row of the stream. `sentiment` and `classification` are null where nothing scored them. */
export const streamHitSchema = z.object({
  /** The warehouse hit id. */
  id: z.string(),
  headline: z.string(),
  mediaType: mediaTypeSchema,
  /** The article's canonical URL — the "link" column. */
  url: z.string(),
  /** The outlet's domain — what coverage-list membership is judged against. */
  domain: z.string(),
  outlet: z.string(),
  author: z.string(),
  /** The folder housing the matching topic; '' when unknown. */
  folder: z.string(),
  /** The primary stream topic that matched this hit — the first attached one — or, for a cached
   *  report row without topic ids, the report section it was curated under. */
  topic: z.string(),
  /** EVERY attached stream topic this hit matched, for multi-topic tagging in the Library — a hit
   *  can belong to several. Starts with `topic`; `[topic]` when only one (or none) matched.
   *  Defaulted so an older client or fixture that omits it still parses. */
  topics: z.array(z.string()).default([]),
  tags: z.array(z.string()),
  /** `tag value → NER entity` for the tags that carry one, so the Library colours tag chips by
   *  entity exactly as Topics does. A companion to `tags` (not a reshape, so facets/filters/sort
   *  keep reading the value list); a tag absent here has no known entity and renders neutral.
   *  Defaulted so an older payload or fixture that omits it still parses. */
  tagEntities: z.record(z.string(), z.string()).default({}),
  /** ISO timestamp — the "date" column. */
  publishedAt: z.string(),
  sentiment: streamSentimentSchema.nullable(),
  classification: z.string().nullable(),
  /** The query id that drove the classification — for filtering / links. Optional column (nullish
   *  so an older payload or fixture that omits it still parses). */
  classificationQueryId: z.string().nullish(),
  /** TTD placement tier (Leading / Listed / Incidental / Unverified). Optional column. */
  placement: z.string().nullish(),
  reach: z.number(),
  snippet: z.string(),
  /** Non-null when the hit is part of a syndication cluster (an origin story and its pickups). */
  syndicationId: z.string().nullable(),
  /** False for a pickup of another outlet's story. */
  isOrigin: z.boolean(),
  /** Similarity to the previous article in the echo chain; null on the origin. */
  echoSimilarity: z.number().nullable(),
  /** The sub-account whose stream this row is in — internal scoping, never a column. */
  client: z.string(),
  // ── Columns the analytics build writes beyond the fixed row (stream-columns.ts). Each is nullish
  // or defaulted, so an older payload, client or fixture that omits them still parses. ──
  /** ISO 3166 alpha-2, as the build wrote it. */
  country: z.string().nullish(),
  language: z.string().nullish(),
  /** Earned media value — the build's estimate (it varies between builds), never a measurement. */
  emv: z.number().nullish(),
  /** The build's brand-mention flag (`Mention`). */
  mention: z.boolean().nullish(),
  /** The narrative cluster the build put the article in (`Narrative`). */
  narrative: z.string().nullish(),
  /** A hit curated by hand into the build (`IsInjected`); absent otherwise. */
  injected: z.boolean().nullish(),
  /**
   * Every other column the build wrote, under the build's own name — a client's custom
   * classifications, Upwork's measurement columns, whatever is added next. A flat object is
   * spread into `Column.key` entries (`ThemeScores.AI`). See `toCustomFields` (analytics-cache.ts)
   * for what is kept and `inferStreamColumns` for what each column is.
   */
  fields: z
    .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]))
    .optional(),
})
export type StreamHit = z.infer<typeof streamHitSchema>

// ── Filters (client-side over one bounded window; the API takes only the window) ──────────────

export const FACET_KEYS = [
  'mediaTypes',
  'folders',
  'topics',
  'tags',
  'outlets',
  'sentiments',
  'classifications',
] as const
export type FacetKey = (typeof FACET_KEYS)[number]

/** The relative windows a page or a request may name. A page's selection is loaded as the
 *  smallest of `STREAM_WINDOW_PRESETS` that holds it. */
export const DAYS_AGO_OPTIONS = [1, 7, 30, 90, 365] as const
export const daysAgoSchema = z.union([
  z.literal(1),
  z.literal(7),
  z.literal(30),
  z.literal(90),
  z.literal(365),
])
export type DaysAgo = z.infer<typeof daysAgoSchema>

/**
 * What the pages filter the rows by. Facets are multi-select (empty array = any), AND across
 * facets, OR within one. Saved views store this shape, so it is a schema, not just a type.
 */
export const streamFiltersSchema = z.object({
  mediaTypes: z.array(mediaTypeSchema).default([]),
  sentiments: z.array(streamSentimentSchema).default([]),
  folders: z.array(z.string()).default([]),
  topics: z.array(z.string()).default([]),
  tags: z.array(z.string()).default([]),
  classifications: z.array(z.string()).default([]),
  outlets: z.array(z.string()).default([]),
  /** Relative window; absent = the default (`DEFAULT_STREAM_DAYS`). Mutually exclusive with
   *  `from`/`to` — setting one clears the other. */
  daysAgo: daysAgoSchema.optional(),
  /** Absolute range, yyyy-mm-dd, both ends inclusive (string-prefix compare). Either may stand
   *  alone. */
  from: z.string().optional(),
  to: z.string().optional(),
  /** Collapse syndication clusters to one row (origin preferred) — a page-wide rule. */
  groupSyndication: z.boolean().optional(),
  search: z.string().optional(),
})
export type StreamFilters = z.infer<typeof streamFiltersSchema>

export const EMPTY_STREAM_FILTERS: StreamFilters = {
  mediaTypes: [],
  sentiments: [],
  folders: [],
  topics: [],
  tags: [],
  classifications: [],
  outlets: [],
}

const facetParam = z.array(z.string().max(500)).max(500).optional().catch(undefined)

/**
 * The Analytics → Library drill-through contract: a chart click opens Library with the page's
 * WHOLE filter set plus the chart's own narrowing in the URL, so a filtered view is linkable and
 * nothing the user narrowed by is silently dropped on the way (CLI-826 — it used to carry only a
 * sentiment and a relative window). Invalid params fall away rather than 404 — a stale link
 * should still open the page. `sentiment` is the pre-CLI-826 single value, still read.
 */
export const streamSearchSchema = z.object({
  sentiment: streamSentimentSchema.optional().catch(undefined),
  daysAgo: daysAgoSchema.optional().catch(undefined),
  mediaTypes: z.array(mediaTypeSchema).max(20).optional().catch(undefined),
  sentiments: z.array(streamSentimentSchema).max(10).optional().catch(undefined),
  folders: facetParam,
  topics: facetParam,
  tags: facetParam,
  classifications: facetParam,
  outlets: facetParam,
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
  groupSyndication: z.boolean().optional().catch(undefined),
  search: z.string().max(500).optional().catch(undefined),
  /** A Coverage Lab click-through: the token of the exact article set behind one part of a chart,
   *  held in this browser (lib/drill.ts) — ids do not fit in a URL. Unknown or expired reads as
   *  absent, and the page says so. */
  drill: z
    .string()
    .regex(/^[a-z0-9]{6,40}$/)
    .optional()
    .catch(undefined),
})
export type StreamSearch = z.infer<typeof streamSearchSchema>

function facetValues(hit: StreamHit, key: FacetKey): string[] {
  switch (key) {
    case 'mediaTypes':
      return [hit.mediaType]
    case 'sentiments':
      return hit.sentiment ? [hit.sentiment] : []
    case 'folders':
      return hit.folder ? [hit.folder] : []
    case 'topics':
      return hit.topics.length > 0 ? hit.topics : [hit.topic]
    case 'tags':
      return hit.tags
    case 'classifications':
      return hit.classification ? [hit.classification] : []
    case 'outlets':
      return [hit.outlet]
  }
}

function passesFacet(hit: StreamHit, key: FacetKey, selected: string[]): boolean {
  if (selected.length === 0) return true
  const values = new Set(facetValues(hit, key))
  return selected.some((value) => values.has(value))
}

/**
 * The pages' filter, over rows already bounded by the API's window. `now` anchors `daysAgo`;
 * render code passes a value captured once per page load so renders stay pure.
 */
export function applyStreamFilters(
  hits: StreamHit[],
  filters: StreamFilters,
  now: number = Date.now(),
): StreamHit[] {
  const cutoff =
    filters.daysAgo === undefined ? undefined : now - filters.daysAgo * 24 * 60 * 60 * 1000
  const needle = filters.search?.trim().toLowerCase()
  const filtered = hits.filter((hit) => {
    if (cutoff !== undefined && Date.parse(hit.publishedAt) < cutoff) return false
    // Absolute bounds compare on the date prefix, inclusive both ends.
    const day = hit.publishedAt.slice(0, 10)
    if (filters.from && day < filters.from) return false
    if (filters.to && day > filters.to) return false
    for (const key of FACET_KEYS) {
      if (!passesFacet(hit, key, filters[key])) return false
    }
    if (
      needle &&
      !hit.headline.toLowerCase().includes(needle) &&
      !hit.snippet.toLowerCase().includes(needle) &&
      !hit.outlet.toLowerCase().includes(needle) &&
      !hit.author.toLowerCase().includes(needle)
    )
      return false
    return true
  })
  return filters.groupSyndication ? collapseSyndication(filtered) : filtered
}

/** One row per syndication cluster — the origin when it survived the filters, else the first
 *  member. Applied AFTER filtering, so the rule never changes what matched. */
export function collapseSyndication(hits: StreamHit[]): StreamHit[] {
  const byCluster = new Map<string, StreamHit>()
  const result: StreamHit[] = []
  for (const hit of hits) {
    if (!hit.syndicationId) {
      result.push(hit)
      continue
    }
    const kept = byCluster.get(hit.syndicationId)
    if (!kept) {
      byCluster.set(hit.syndicationId, hit)
      result.push(hit)
    } else if (hit.isOrigin && !kept.isOrigin) {
      // Replace in place so the cluster keeps its position in the (date-sorted) stream.
      result[result.indexOf(kept)] = hit
      byCluster.set(hit.syndicationId, hit)
    }
  }
  return result
}

/**
 * Per-facet option counts: each facet's counts are computed with every OTHER filter applied but
 * its own skipped, so ticking an option never zeroes out its siblings.
 */
export function facetCounts(
  hits: StreamHit[],
  filters: StreamFilters,
  key: FacetKey,
  now?: number,
): Map<string, number> {
  const withoutOwn: StreamFilters = { ...filters, [key]: [] }
  const scoped = applyStreamFilters(hits, withoutOwn, now)
  const counts = new Map<string, number>()
  for (const hit of scoped) {
    for (const value of facetValues(hit, key)) {
      counts.set(value, (counts.get(value) ?? 0) + 1)
    }
  }
  return counts
}

/** Every option a facet can offer, from the (unfiltered) rows — stable while filters change. */
export function facetOptions(hits: StreamHit[], key: FacetKey): string[] {
  const values = new Set<string>()
  for (const hit of hits) for (const value of facetValues(hit, key)) values.add(value)
  return [...values].sort((a, b) => a.localeCompare(b))
}

// ── Stream configuration ──────────────────────────────────────────────────────────────────────

/**
 * A topic feeding the stream — the report header junction with the per-topic config that used to
 * be stream-wide (CLI-758). Empty `dataSources` = every source the topic collects;
 * `includeSyndications` and `coverage` are this topic's own. `coverage` is the INPUT shape here
 * (rules without ids); the stored config returns `streamTopicConfigSchema`, whose coverage carries
 * ids. A field left at its default (true / no rules) means the stream's own default applies.
 */
export const streamTopicJunctionSchema = z.object({
  topicId: z.uuid(),
  dataSources: z.array(z.string().min(1)).max(10).default([]),
  /** Pull this topic's syndicated copies into the stream. Default true (the stream's own default). */
  includeSyndications: z.boolean().default(true),
  /** This topic's own coverage rules on the stream — outlets, countries, languages — which replace
   *  the stream's per field, the way a report header overrides its report. Empty = inherit. */
  coverage: z.array(coverageRuleInputSchema).default([]),
})
export type StreamTopicJunction = z.infer<typeof streamTopicJunctionSchema>
/** What a caller sends: `dataSources`, `includeSyndications` and `coverage` may be left out. */
export type StreamTopicJunctionInput = z.input<typeof streamTopicJunctionSchema>

/** A topic junction as the stored config returns it: coverage rules carry their ids. */
export const streamTopicConfigSchema = streamTopicJunctionSchema.extend({
  coverage: z.array(coverageRuleSchema),
})
export type StreamTopicConfig = z.infer<typeof streamTopicConfigSchema>

/**
 * How the analytics cache builds a stream: `reports` — what the workspace's reports delivered,
 * the curated read — `semantic` — live matching of the stream's topics, the read the legacy
 * webapp's live tier has — or `both`, the union of the two (the MV_PLUS_REPORT equivalent). The
 * one DASHBOARD-WIDE setting the cache job reads (analytics-dashboard-backend; the successor of
 * its hardcoded live list); Headline serves every mode from the job's cached payload and never
 * reads the warehouse for a stream, so nothing here branches on it.
 */
export const STREAM_MODES = ['reports', 'semantic', 'both'] as const
export const streamModeSchema = z.enum(STREAM_MODES)
export type StreamMode = z.infer<typeof streamModeSchema>

export const STREAM_MODE_LABELS: Record<StreamMode, string> = {
  reports: 'Report-curated',
  semantic: 'Semantic',
  both: 'Report-curated + semantic',
}

/**
 * What the API stores and returns: rules carry their ids, like a report's. `coverage` is the
 * stream's own DEFAULT rules; each topic in `topics` carries its own `coverage` override and
 * `includeSyndications`. `includeSyndications` at this level is the stream-wide default the read
 * path falls back to for rows no attached topic claims (CLI-758).
 */
export const streamConfigSchema = z.object({
  topics: z.array(streamTopicConfigSchema),
  coverage: z.array(coverageRuleSchema),
  mode: streamModeSchema,
  /** The stream-wide syndication default — applied to rows not attributable to an attached topic
   *  (every row until the cache job stamps topic ids). A topic's own `includeSyndications` wins for
   *  its rows. Distinct from the pages' "Group syndication" toggle, which only collapses display. */
  includeSyndications: z.boolean(),
})
export type StreamConfig = z.infer<typeof streamConfigSchema>

export const EMPTY_STREAM_CONFIG: StreamConfig = {
  topics: [],
  coverage: [],
  mode: 'reports',
  includeSyndications: true,
}

/**
 * `PUT /api/streams/:subaccountId/config` — the whole configuration, replaced. The syndication
 * rule is now per topic (on each junction), so this no longer carries a stream-wide
 * `includeSyndications`: the stored stream default is frozen and only read as the fallback.
 */
export const streamConfigInputSchema = z.object({
  topics: z.array(streamTopicJunctionSchema).max(200),
  coverage: z.array(coverageRuleInputSchema).max(500),
  mode: streamModeSchema.default('reports'),
})
export type StreamConfigInput = z.infer<typeof streamConfigInputSchema>

// ── KPI aggregates ───────────────────────────────────────────────────────────────────────────

/**
 * What the Analytics KPI tiles compute, in ONE place for both sides: the page computes a tile's
 * value from the rows it holds, the API computes the same aggregates for the period before the
 * loaded window (`prior` on the hits response) from rows the page never receives — and a tile's
 * change is the one against the other. One function for both, so the two can never disagree on
 * what "net sentiment" or "reach" means.
 */
export const KPI_METRICS = ['kpiMentions', 'kpiNetSentiment', 'kpiNegative', 'kpiReach'] as const
export type KpiMetric = (typeof KPI_METRICS)[number]

/**
 * Rows counted by media type and sentiment — everything the four KPI metrics need (mentions,
 * reach, the sentiment counts), kept per cell so a tile narrowed to a media type or a sentiment
 * reads its own slice. At most 5 media types x 5 sentiments (four, and unscored) = 25 cells.
 */
export const streamSummaryCellSchema = z.object({
  mediaType: mediaTypeSchema,
  /** Null = rows nothing scored. */
  sentiment: streamSentimentSchema.nullable(),
  mentions: z.number().int().nonnegative(),
  reach: z.number().nonnegative(),
})
export const streamSummarySchema = z.object({ cells: z.array(streamSummaryCellSchema).max(25) })
export type StreamSummary = z.infer<typeof streamSummarySchema>

export function emptyStreamSummary(): StreamSummary {
  return { cells: [] }
}

/** Count one row into a summary (in place — the API folds tens of thousands without keeping them). */
export function addToStreamSummary(
  summary: StreamSummary,
  hit: Pick<StreamHit, 'mediaType' | 'sentiment' | 'reach'>,
): void {
  let cell = summary.cells.find(
    (c) => c.mediaType === hit.mediaType && c.sentiment === hit.sentiment,
  )
  if (!cell) {
    cell = { mediaType: hit.mediaType, sentiment: hit.sentiment, mentions: 0, reach: 0 }
    summary.cells.push(cell)
  }
  cell.mentions += 1
  cell.reach += hit.reach
}

export function summarizeStreamHits(
  hits: Iterable<Pick<StreamHit, 'mediaType' | 'sentiment' | 'reach'>>,
): StreamSummary {
  const summary = emptyStreamSummary()
  for (const hit of hits) addToStreamSummary(summary, hit)
  return summary
}

/** The slice of a summary to total: the page's media-type and sentiment facets, and a tile's own
 *  narrowing. Empty or absent = any, as in `applyStreamFilters`; a sentiment selection leaves
 *  unscored rows out, as the facet does. */
export interface StreamSummarySelection {
  mediaTypes?: readonly MediaType[] | undefined
  sentiments?: readonly StreamSentiment[] | undefined
}

export interface StreamTotals {
  mentions: number
  reach: number
  positive: number
  neutral: number
  negative: number
  mixed: number
  unscored: number
}

export function streamTotals(
  summary: StreamSummary,
  selection: StreamSummarySelection = {},
): StreamTotals {
  const media = selection.mediaTypes?.length ? new Set(selection.mediaTypes) : null
  const sentiments = selection.sentiments?.length ? new Set(selection.sentiments) : null
  const totals: StreamTotals = {
    mentions: 0,
    reach: 0,
    positive: 0,
    neutral: 0,
    negative: 0,
    mixed: 0,
    unscored: 0,
  }
  for (const cell of summary.cells) {
    if (media && !media.has(cell.mediaType)) continue
    if (sentiments && (cell.sentiment === null || !sentiments.has(cell.sentiment))) continue
    totals.mentions += cell.mentions
    totals.reach += cell.reach
    totals[cell.sentiment ?? 'unscored'] += cell.mentions
  }
  return totals
}

/** A KPI metric's number: mentions, summed reach, negative mentions, or net sentiment — positive
 *  minus negative as a share of ALL mentions in the slice (unscored rows count in the base). */
export function kpiValue(metric: KpiMetric, totals: StreamTotals): number {
  switch (metric) {
    case 'kpiReach':
      return totals.reach
    case 'kpiNegative':
      return totals.negative
    case 'kpiNetSentiment':
      return totals.mentions === 0 ? 0 : (totals.positive - totals.negative) / totals.mentions
    case 'kpiMentions':
      return totals.mentions
  }
}

export interface KpiTrend {
  /** Signed change: percent for counts and reach, POINTS for net sentiment. Null = nothing to
   *  compare against. */
  delta: number | null
  /** Whether the movement is welcome — negative mentions going UP is red, not green. */
  good: boolean
  /** e.g. "+12%" or "−0.08". */
  label: string
}

const NO_TREND: KpiTrend = { delta: null, good: true, label: '' }

/**
 * A metric now against the same metric over the period before. No trend when there is no prior
 * period to read (`before` null), when it held nothing (a change against nothing is not a
 * number), or — for counts and reach — when the prior value is zero.
 */
export function kpiTrend(
  metric: KpiMetric,
  now: StreamTotals,
  before: StreamTotals | null,
): KpiTrend {
  if (!before || before.mentions === 0) return NO_TREND
  const current = kpiValue(metric, now)
  const previous = kpiValue(metric, before)
  if (metric === 'kpiNetSentiment') {
    const delta = current - previous
    return {
      delta,
      good: delta >= 0,
      label: `${delta >= 0 ? '+' : '−'}${Math.abs(delta).toFixed(2)}`,
    }
  }
  if (previous === 0) return NO_TREND
  const delta = ((current - previous) / previous) * 100
  const up = delta >= 0
  return {
    delta,
    // More negative mentions is bad news; everything else up is good news.
    good: metric === 'kpiNegative' ? !up : up,
    label: `${up ? '+' : '−'}${Math.abs(Math.round(delta))}%`,
  }
}

// ── Rows ─────────────────────────────────────────────────────────────────────────────────────

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * The most rows one read returns; the page filters client-side within them. Sized from the
 * webapp parity run of 2026-10-05: the busiest client had 13,745 rows in 30 days and 32 of 45
 * clients passed 5,000 in a year, so a 5,000 cap cut Analytics to two to four weeks for the
 * busiest eight. 25,000 rows are 14.1 MB of JSON, 2.6 MB gzipped (StockX's newest 25,000,
 * measured 2026-10-05: about 590 bytes a row) — the route compresses; StockX's default 30-day
 * window is 6,201 rows, 3.7 MB before compression.
 */
export const STREAM_HITS_CAP = 25_000

/** The window a page opens on, and a request without one gets: the webapp's default. */
export const DEFAULT_STREAM_DAYS = 30

/**
 * The relative windows a page LOADS: the smallest that holds its date selection (24h and 7d are
 * filtered out of the 30-day load in the browser). Three, so the rows' query key takes three
 * values per workspace however the reader clicks.
 */
export const STREAM_WINDOW_PRESETS = [30, 90, 365] as const
export type StreamWindowPreset = (typeof STREAM_WINDOW_PRESETS)[number]

/** What a page asks the API for: a preset, or the reader's own date range (yyyy-mm-dd, either
 *  end may be open). */
export type StreamLoadWindow =
  | { kind: 'preset'; days: StreamWindowPreset }
  | { kind: 'range'; from: string | undefined; to: string | undefined }

export const DEFAULT_STREAM_LOAD_WINDOW: StreamLoadWindow = {
  kind: 'preset',
  days: DEFAULT_STREAM_DAYS,
}

/**
 * The window the SERVER applies for a page's date selection. The selection used to be a filter
 * over one year of rows loaded up front; a year of a busy client does not fit any sane cap, so
 * the selection now decides what is read, and the cap falls inside the window the reader asked
 * for.
 */
export function streamLoadWindow(
  filters: Pick<StreamFilters, 'daysAgo' | 'from' | 'to'>,
): StreamLoadWindow {
  if (filters.from || filters.to) {
    return { kind: 'range', from: filters.from || undefined, to: filters.to || undefined }
  }
  const days = filters.daysAgo ?? DEFAULT_STREAM_DAYS
  return { kind: 'preset', days: STREAM_WINDOW_PRESETS.find((preset) => preset >= days) ?? 365 }
}

/** True when the page's date selection is something other than the default window. */
export function dateNarrowed(filters: Pick<StreamFilters, 'daysAgo' | 'from' | 'to'>): boolean {
  if (filters.from || filters.to) return true
  return filters.daysAgo !== undefined && filters.daysAgo !== DEFAULT_STREAM_DAYS
}

const isDay = (value: string | undefined): value is string =>
  value !== undefined && /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value))

/**
 * The request's query for a load window. A range is day-granular and inclusive at both ends, in
 * UTC — the way the pages compare a row's date — so `to` becomes the start of the next day, the
 * API's exclusive bound. An open start reads the year before the end; an open end reads to now.
 * A date that is not one (a half-typed input) is ignored rather than sent.
 */
export function streamLoadQuery(window: StreamLoadWindow): {
  daysAgo?: string
  from?: string
  to?: string
} {
  if (window.kind === 'preset') return { daysAgo: String(window.days) }
  const from = isDay(window.from) ? Date.parse(`${window.from}T00:00:00.000Z`) : undefined
  const to = isDay(window.to) ? Date.parse(`${window.to}T00:00:00.000Z`) + DAY_MS : undefined
  if (from === undefined && to === undefined) return { daysAgo: String(DEFAULT_STREAM_DAYS) }
  if (from !== undefined && to !== undefined && from >= to) {
    // An inverted range holds nothing; ask for the end day alone rather than a 400.
    return { from: new Date(to - DAY_MS).toISOString(), to: new Date(to).toISOString() }
  }
  return {
    from: new Date(from ?? (to as number) - 365 * DAY_MS).toISOString(),
    ...(to !== undefined && { to: new Date(to).toISOString() }),
  }
}

/**
 * `GET /api/streams/:subaccountId/hits` — one bounded window, newest first: the window the page
 * selected, applied on the server. `daysAgo` (default `DEFAULT_STREAM_DAYS`) or an absolute
 * `from`/`to`; both together is a 400.
 */
export const streamHitsQuerySchema = z
  .object({
    daysAgo: z.coerce.number().pipe(daysAgoSchema).optional(),
    from: z.iso.datetime({ offset: true }).optional(),
    to: z.iso.datetime({ offset: true }).optional(),
  })
  .refine((q) => q.daysAgo === undefined || (q.from === undefined && q.to === undefined), {
    message: 'daysAgo and from/to are alternatives.',
  })
export type StreamHitsQuery = z.infer<typeof streamHitsQuerySchema>

export const streamHitsResponseSchema = z.object({
  hits: z.array(streamHitSchema),
  /** Rows were left out past the cap, so the page shows a bounded window, not an exact total. */
  hasMore: z.boolean(),
  /**
   * The oldest moment the served rows cover: `window.from` when everything in the window was
   * served, else the oldest served row's publish time. Trends and timelines older than this are
   * not knowable from the served set, and the pages say so.
   */
  coveredFrom: z.iso.datetime({ offset: true }),
  /** False when no report of this sub-account has a cached payload yet: the page says so, and
   *  nothing is read from anywhere else. */
  cached: z.boolean(),
  /** When the stalest payload read was computed — "as of". Null when nothing was cached. */
  computedAt: z.iso.datetime({ offset: true }).nullable(),
  /** The stream's setting, for display: what the cache job builds it from. It changes nothing
   *  about how these rows were read — every stream is served from the cached payload. */
  mode: streamModeSchema,
  /**
   * The KPI aggregates of the period immediately BEFORE the window, of the same length and under
   * the same stream rules (topics, coverage, syndication) — what the tiles' change is measured
   * against, without loading twice the rows. Null when there is no honest comparison: the cached
   * payloads do not reach back to that period's start, or the window itself was cut by the cap.
   */
  prior: streamSummarySchema.nullable(),
  /**
   * Whether the cached rows carry the topic ids the job stamps. False (every payload on
   * 2026-10-05) means the stream's topics cannot narrow the rows yet — every row the reports
   * curated is served — and a row's "topic" is the report section it was curated under. The
   * pages say so instead of promising a topic filter that does nothing.
   */
  topicsStamped: z.boolean(),
  window: z.object({
    from: z.iso.datetime({ offset: true }),
    to: z.iso.datetime({ offset: true }),
  }),
})
export type StreamHitsResponse = z.infer<typeof streamHitsResponseSchema>

/** The window a request names, resolved against `now`. */
export function streamWindow(
  query: StreamHitsQuery,
  now: Date = new Date(),
): { from: Date; to: Date } {
  if (query.from !== undefined || query.to !== undefined) {
    const to = query.to !== undefined ? new Date(query.to) : now
    const from =
      query.from !== undefined
        ? new Date(query.from)
        : new Date(to.getTime() - DEFAULT_STREAM_DAYS * DAY_MS)
    return { from, to }
  }
  return {
    from: new Date(now.getTime() - (query.daysAgo ?? DEFAULT_STREAM_DAYS) * DAY_MS),
    to: now,
  }
}

/**
 * The Library's bulk Remove (CLI-625): many hits, one decision, `account`-scoped — Kevin,
 * 2026-10-07: a removal from the Library takes the story out of everything for that customer
 * (Library, Analytics, every team's reports and RTAs). Reversible underneath (a removal is a row,
 * never a hard delete), so the response carries the ids: Undo can be added as UI alone later.
 */
/** Hits per removal call. The Library sends a larger selection in batches of this size. */
export const STREAM_REMOVE_BATCH = 500

export const removeStreamHitsSchema = z.object({
  /** Warehouse hit ids, as the stream rows carry them. */
  hitIds: z.array(z.uuid()).min(1).max(STREAM_REMOVE_BATCH),
  reason: z
    .enum(['irrelevant', 'duplicate', 'wrong_outlet', 'client_request', 'legal', 'other'])
    .optional(),
  note: z.string().max(2000).optional(),
})
export type RemoveStreamHits = z.infer<typeof removeStreamHitsSchema>

export const removeStreamHitsResponseSchema = z.object({
  /** Hit id → deletion id, for the rows this call recorded. */
  recorded: z.array(z.object({ hitId: z.uuid(), deletionId: z.uuid() })),
  /** Hits that were already removed for this account; nothing was written for them. */
  already: z.array(z.object({ hitId: z.uuid(), deletionId: z.uuid() })),
})
export type RemoveStreamHitsResponse = z.infer<typeof removeStreamHitsResponseSchema>
